/**
 * SDK Configuration Types
 * Matches backend SDKConfiguration model
 */

export type SDKType = 'storefront' | 'pricing' | 'product' | 'checkout';

export type ThemeMode = 'light' | 'dark';
export type BorderRadius = 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
export type LayoutStyle = 'grid' | 'list';
export type LayoutColumns = 1 | 2 | 3 | 4;
export type LayoutSpacing = 'compact' | 'comfortable' | 'spacious';
export type CardStyle = 'flat' | 'elevated' | 'outlined' | 'glass';
export type HoverAnimation = 'none' | 'lift' | 'scale' | 'glow';
export type TransitionStyle = 'none' | 'subtle' | 'smooth' | 'bouncy';
export type HeadingWeight = 400 | 500 | 600 | 700 | 800;
export type BodySize = 'sm' | 'md' | 'lg';

export interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textMuted: string;
  border: string;
}

export interface Theme {
  mode: ThemeMode;
  colors: ThemeColors;
  borderRadius: BorderRadius;
}

export interface Typography {
  fontFamily: string;
  headingWeight: HeadingWeight;
  bodySize: BodySize;
}

export interface Layout {
  style: LayoutStyle;
  columns: LayoutColumns;
  spacing: LayoutSpacing;
  cardStyle: CardStyle;
}

export interface Effects {
  hoverAnimation: HoverAnimation;
  transitions: TransitionStyle;
}

export interface Features {
  showPrices: boolean;
  showRatings: boolean;
  showDescription: boolean;
  quickBuy: boolean;
  showBadges: boolean;
}

export interface SDKConfig {
  type: SDKType;
  theme: Theme;
  typography: Typography;
  layout: Layout;
  effects: Effects;
  features: Features;
}

export interface StoredSDKConfig extends SDKConfig {
  id: number;
  educator_id: number;
  sdk_type: SDKType;
  name?: string;
  is_active: boolean;
  is_published: boolean;
  version: number;
  created_at: string;
  updated_at: string;
  published_at?: string;
}

/**
 * Default configuration values
 */
export const DEFAULT_THEME_COLORS: ThemeColors = {
  primary: '#6366F1',
  secondary: '#8B5CF6',
  accent: '#F59E0B',
  background: '#FFFFFF',
  surface: '#F9FAFB',
  text: '#111827',
  textMuted: '#6B7280',
  border: '#E5E7EB',
};

export const DEFAULT_CONFIG: SDKConfig = {
  type: 'storefront',
  theme: {
    mode: 'light',
    colors: DEFAULT_THEME_COLORS,
    borderRadius: 'lg',
  },
  typography: {
    fontFamily: 'Inter, system-ui, sans-serif',
    headingWeight: 600,
    bodySize: 'md',
  },
  layout: {
    style: 'grid',
    columns: 4,
    spacing: 'comfortable',
    cardStyle: 'elevated',
  },
  effects: {
    hoverAnimation: 'lift',
    transitions: 'smooth',
  },
  features: {
    showPrices: true,
    showRatings: true,
    showDescription: false,
    quickBuy: true,
    showBadges: true,
  },
};

/**
 * Convert border radius value to CSS
 */
export function getBorderRadiusCss(radius: BorderRadius): string {
  const map: Record<BorderRadius, string> = {
    none: '0',
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  };
  return map[radius];
}

/**
 * Convert spacing value to CSS gap
 */
export function getSpacingCss(spacing: LayoutSpacing): string {
  const map: Record<LayoutSpacing, string> = {
    compact: '0.75rem',
    comfortable: '1.25rem',
    spacious: '2rem',
  };
  return map[spacing];
}

/**
 * Convert body size to CSS font size
 */
export function getBodySizeCss(size: BodySize): string {
  const map: Record<BodySize, string> = {
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
  };
  return map[size];
}
